interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha: Grecaptcha;
  }
}

const RECAPTCHA_SCRIPT_ID = "recaptcha-script";

let loadPromise: Promise<void> | null = null;

function loadRecaptchaScript(): Promise<void> {
  if (loadPromise) return loadPromise;

  if (window.grecaptcha && document.getElementById(RECAPTCHA_SCRIPT_ID)) {
    loadPromise = new Promise((resolve) => {
      window.grecaptcha.ready(() => resolve());
    });
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existingScript) {
      window.grecaptcha.ready(() => resolve());
      return;
    }

    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      reject(new Error("VITE_RECAPTCHA_SITE_KEY is not configured"));
      return;
    }

    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://recaptcha.net/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => resolve());
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

export async function executeRecaptcha(action: string): Promise<string> {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    console.warn("reCAPTCHA site key is not configured, skipping verification");
    return "";
  }

  await loadRecaptchaScript();
  return window.grecaptcha.execute(siteKey, { action });
}
